import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { ShopModal } from "@/components/game/ShopModal";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ACHIEVEMENTS, WEAPON_ORDER, MAIN_LEVELS, BESTIARY } from "@/game/config";
import { EnemyGuideModal } from "@/components/game/EnemyGuide";
import { TitleScreen } from "@/components/game/TitleScreen";
import { GameScreen } from "@/components/game/GameScreen";
import { GameOverScreen, DailyResultScreen } from "@/components/game/GameOverScreen";
import { EndingScene } from "@/components/game/EndingScene";
import { AchievementsModal, LeaderboardModal } from "@/components/game/Modals";

function App() {
  const [screen, setScreen] = useState("title");
  const [name, setName] = useState(localStorage.getItem("tbq_name") || "");
  const [difficulty, setDifficulty] = useState(localStorage.getItem("tbq_diff") || "easy");
  const [unlocked, setUnlocked] = useState([]);
  const [stats, setStats] = useState(null);
  const [runId, setRunId] = useState(0);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const unlockedRef = useRef([]);
  const bestiaryRef = useRef([]);
  const [bestiary, setBestiary] = useState([]);
  const [player, setPlayer] = useState(null);
  const [runOpts, setRunOpts] = useState({});
  const lastStart = useRef(1);
  const [dailyResult, setDailyResult] = useState(null);
  const [lbTab, setLbTab] = useState("all");
  const [mode, setMode] = useState(localStorage.getItem("tbq_mode") || "side");

  const loadPlayer = async (n) => {
    const p = await api.upsertPlayer(n);
    unlockedRef.current = p.achievements;
    setUnlocked(p.achievements);
    bestiaryRef.current = p.bestiary || [];
    setBestiary(bestiaryRef.current);
    setPlayer(p);
    return p;
  };

  useEffect(() => {
    const path = window.location.pathname;
    if (name) loadPlayer(name).catch(() => {});
    if (path === "/payment/cancel") { toast.info("Checkout cancelled."); window.history.replaceState({}, "", "/"); }
    if (path !== "/payment/success") return;
    const sid = new URLSearchParams(window.location.search).get("session_id");
    window.history.replaceState({}, "", "/");
    let tries = 0;
    const poll = async () => {
      try {
        const s = await api.paymentStatus(sid);
        if (s.payment_status === "paid") {
          toast.success(`Payment complete! +${s.gems} gems`);
          if (name) { await loadPlayer(name); setModal("shop"); }
          return;
        }
        if (s.status === "expired" || s.payment_status === "failed") { toast.error("Payment did not go through."); return; }
      } catch (e) { /* retry */ }
      if (++tries < 15) setTimeout(poll, 2000); else toast.info("Payment still processing. Gems will appear shortly.");
    };
    toast("Confirming your payment...");
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openShop = async (n) => {
    try { await loadPlayer(n); setName(n); localStorage.setItem("tbq_name", n); setModal("shop"); }
    catch (e) { toast.error("Could not load your gems."); }
  };

  const start = async (n, diff, level = 1, m = mode) => {
    setLoading(true);
    try {
      await loadPlayer(n);
      const { loadout, player: p } = await api.startRun(n);
      setPlayer(p);
      lastStart.current = level;
      const weapon = [...WEAPON_ORDER].reverse().find((w) => p.weapons.includes(w)) || "butter_knife";
      setRunOpts({ startLevel: level, weapon, extraHearts: p.extra_hearts, extraLives: loadout.extra_lives, bonusPack: loadout.bonus_pack, skin: p.equipped_skin });
      if (loadout.extra_lives || loadout.bonus_pack) toast.success(`Loadout: +${loadout.extra_lives} lives${loadout.bonus_pack ? " · Bonus Pack active" : ""}`);
      setName(p.name); setDifficulty(diff); setMode(m); localStorage.setItem("tbq_mode", m);
      localStorage.setItem("tbq_name", p.name); localStorage.setItem("tbq_diff", diff);
      setRunId((r) => r + 1);
      setScreen("game");
    } catch (e) {
      toast.error("Could not reach the toast server. Try again.");
    } finally { setLoading(false); }
  };

  const startDaily = async (n) => {
    setLoading(true);
    try {
      const p = await loadPlayer(n);
      const d = await api.daily();
      setName(p.name); localStorage.setItem("tbq_name", p.name);
      setRunOpts({ startLevel: d.level, daily: d, skin: p.equipped_skin });
      setDailyResult(null);
      setRunId((r) => r + 1);
      setScreen("game");
    } catch (e) {
      toast.error("Could not load today's challenge.");
    } finally { setLoading(false); }
  };

  const finishDaily = (s, completed) => {
    const timeBonus = completed ? Math.max(0, 3000 - s.time * 10) : 0;
    const st = { ...s, score: s.score + timeBonus, completed };
    setStats(st);
    setScreen("daily");
    api.submitDaily({ name, score: st.score, time: s.time, completed }).then((r) => { setDailyResult(r); if (r.reward) { toast.success(`Daily streak ${r.streak}! +${r.reward} gems`); loadPlayer(name).catch(() => {}); } }).catch(() => toast.error("Could not save daily score"));
  };

  const openAchievements = async (n) => {
    if (n.trim()) await loadPlayer(n.trim()).catch(() => {});
    setModal("ach");
  };

  const onAchieve = useCallback((id) => {
    if (unlockedRef.current.includes(id)) return;
    unlockedRef.current = [...unlockedRef.current, id];
    setUnlocked(unlockedRef.current);
    const a = ACHIEVEMENTS.find((x) => x.id === id);
    toast.success(`Achievement: ${a.name}`, { description: a.desc });
    api.addAchievements(name, [id]).catch(() => {});
  }, [name]);

  const onDefeat = useCallback((id) => {
    if (bestiaryRef.current.includes(id)) return;
    const entry = BESTIARY.find((b) => b.id === id);
    if (!entry) return;
    bestiaryRef.current = [...bestiaryRef.current, id];
    setBestiary(bestiaryRef.current);
    toast(`Enemy Guide: ${entry.name} unlocked!`, { description: "Check the Enemy Guide on the title screen for fun facts." });
    api.addBestiary(name, [id]).then(setPlayer).catch(() => {});
    if (bestiaryRef.current.length >= 5) onAchieve("guide_5");
    if (BESTIARY.every((b) => bestiaryRef.current.includes(b.id))) onAchieve("guide_all");
  }, [name, onAchieve]);

  const openGuide = async (n) => {
    if (n) await loadPlayer(n).catch(() => {});
    setModal("guide");
  };

  const finishRun = (s, won) => {
    setStats(s);
    setScreen(won ? "ending" : "gameover");
    api.submitScore({ name, score: s.score, level_reached: s.level, difficulty: s.difficulty, won, toast: s.toast, kills: s.kills }).catch(() => {});
  };

  const retry = () => start(name, difficulty, lastStart.current);

  return (
    <div className="App grain" data-testid="app-root">
      {screen === "title" && (
        <TitleScreen
          initialName={name}
          initialDiff={difficulty}
          initialMode={mode}
          loading={loading}
          onStart={start}
          onLeaderboard={() => { setLbTab("all"); setModal("lb"); }}
          onDaily={startDaily}
          onAchievements={() => openAchievements(name)}
          onGuide={openGuide}
          onShop={openShop}
          player={player}
        />
      )}
      {screen === "game" && (
        <GameScreen
          key={runId}
          difficulty={runOpts.daily ? "normal" : difficulty}
          mode={runOpts.daily ? "side" : mode}
          opts={runOpts}
          onAchieve={onAchieve}
          onDefeat={onDefeat}
          onGameOver={(s) => (runOpts.daily ? finishDaily(s, false) : finishRun(s, false))}
          onDailyComplete={(s) => finishDaily(s, true)}
          onVictory={(s) => finishRun(s, true)}
          onQuit={() => setScreen("title")}
        />
      )}
      {screen === "gameover" && <GameOverScreen stats={stats} onRetry={retry} onHome={() => setScreen("title")} />}
      {screen === "daily" && <DailyResultScreen stats={stats} result={dailyResult} onLeaderboard={() => { setLbTab("daily"); setModal("lb"); }} onHome={() => setScreen("title")} />}
      {screen === "ending" && <EndingScene stats={stats} playerName={name} onContinue={stats.level === MAIN_LEVELS ? () => { onAchieve("bonus_start"); start(name, difficulty, MAIN_LEVELS + 1, "side"); } : null} onReplay={() => start(name, difficulty, 1)} onHome={() => setScreen("title")} />}
      <AchievementsModal open={modal === "ach"} onClose={() => setModal(null)} unlocked={unlocked} playerName={name} />
      <EnemyGuideModal open={modal === "guide"} onClose={() => setModal(null)} found={bestiary} playerName={name} />
      <LeaderboardModal open={modal === "lb"} initialTab={lbTab} onClose={() => setModal(null)} />
      <ShopModal open={modal === "shop"} onClose={() => setModal(null)} player={player} onPlayer={setPlayer} />
      <Toaster position="top-center" richColors offset={{ top: 92 }} mobileOffset={{ top: 70 }} duration={2500} />
    </div>
  );
}

export default App;
